import { AgentMessageItem } from './AgentMessageItem'
import { LeadMessageItem } from './LeadMessageItem'
import { CallAgentItem } from './CallAgentItem'
import { CallSummaryAgentItem } from './CallSummaryAgentItem'
import { AppointmentScheduledItem } from './AppointmentScheduledItem'
import { AppointmentMissedItem } from './AppointmentMissedItem'
import { AppointmentRescheduledItem } from './AppointmentRescheduledItem'
import {
   FC,
   useLayoutEffect,
   useRef,
   useCallback,
   useEffect,
   useState,
   useMemo,
} from 'react'
import { MessageInput } from './MessageInput'
import { StageChangeItem } from './StageChangeItem'
import useConversation from '@forwardflow/hooks/customHooks/conversation/useConversation'
import { Spinner } from '@forwardflow/ui'
import { useFeatureFlag } from 'configcat-react'
import { Button, message, Switch } from 'antd'
import { EmailSentItem } from './EmailSentItem'
import { CallLeadItem } from './CallLeadItem'
import { LeadDetailData } from '@forwardflow/services/LeadService'
import { VoiceMissedItem } from './VoiceMissedItem'
import { ENV } from '@forwardflow/helpers/configs'
import { useAuth } from '@forwardflow/context/AuthContext'
import LocalStorage from '@forwardflow/services/LocalStorage'
import { ConversationService } from '@forwardflow/services/ConversationService'
import { useContactDetails } from '@forwardflow/hooks/customHooks/conversation/useContactDetails'
import { useParams } from 'react-router-dom'
import { useConversationStore } from '@forwardflow/store/conversationStore'
import { AgentLoaderItem } from './AgentLoaderItem'
import { NotesItem } from './NotesItem'
import { InitialLeadSummaryItem } from './InitialSummaryItem'
import { EventItem } from './EventItem'
import { CallSummaryLeadItem } from './CallSummaryLeadItem'
import { useGetDemoIncomingCallStatus } from '@forwardflow/hooks/customHooks/leads/useDemoIncomingCallStatus'
import { useSetAppointmentStatus } from '@forwardflow/hooks/customHooks/leads/useSetAppointmentStatus'
import { useDemoInboxAppEnabled } from '@forwardflow/hooks/customHooks/demoConversation/useDemoInboxAppEnabled'
import { useDemoInboxAppLead } from '@forwardflow/hooks/customHooks/demoConversation/useDemoInboxAppLead'

interface ConversationProps {
   selectedLeadDetails: LeadDetailData | undefined | null
}

const Conversation: FC<ConversationProps> = ({ selectedLeadDetails }) => {
   const { id } = useParams()
   const [isUpdatingDemo, setIsUpdatingDemo] = useState(false)
   const [isAppointmentStatusUpdating, setIsAppointmentStatusUpdating] =
      useState(false)
   const [optimisticMessages, setOptimisticMessages] = useState<{
      [leadId: string]: any[]
   }>({})
   const [isNextConversationLoading, setIsNextConversationLoading] =
      useState(false)
   const { data: demoStatus, mutate: mutateDemoStatus } =
      useGetDemoIncomingCallStatus({
         leadId: selectedLeadDetails?.userInfo.id,
         shouldFetch: !!selectedLeadDetails?.userInfo.id,
      })

   const { data: appointmentStatus, mutate: mutateAppointmentStatus } =
      useSetAppointmentStatus({
         leadId: selectedLeadDetails?.userInfo.id,
         shouldFetch: !!selectedLeadDetails?.userInfo.id,
      })

   const loaderTimeoutRef = useRef<NodeJS.Timeout | null>(null)
   const [conversationLimit, setConversationLimit] = useState<number>(10)
   const scrollRef = useRef<HTMLDivElement>(null)
   const isLoadingMore = useRef<boolean>(false)
   const shouldScrollToBottom = useRef<boolean>(false)
   const isInitialLoad = useRef<boolean>(true)
   const previousScrollBottom = useRef<number>(0)
   const { value: conversationInputValue } = useFeatureFlag(
      'conversationInput',
      false
   )
   const { mutate: mutateLeadDetails } = useContactDetails(
      id ? Number(id) : null
   )
   const {
      auth: { user },
   } = useAuth()
   const { conversation, loading, error, hasMore, loadMore, mutate } =
      useConversation(
         {
            limit: conversationLimit,
            page: 1,
         },
         selectedLeadDetails?.userInfo.id,
         () => {
            shouldScrollToBottom.current = true
            isInitialLoad.current = false
         }
      )
   const { updateConversation, setLastUpdate } = useConversationStore()
   const [phoneNumber, setPhoneNumber] = useState(
      selectedLeadDetails?.agentInfo?.demoRealtorPhone || ''
   )
   const [isSavingPhone, setIsSavingPhone] = useState(false)

   const isOldDemo = window.location.pathname.includes('demo-conversations-old')
   const leadId = selectedLeadDetails?.userInfo?.id

   const {
      data: demoInboxAppEnabledData,
      isLoading: isLoadingDemoInboxAppEnabled,
      mutate: mutateDemoInboxAppEnabled,
   } = useDemoInboxAppEnabled(isOldDemo ? leadId : undefined)

   const { data: demoInboxAppLeadData, mutate: mutateDemoInboxAppLead } =
      useDemoInboxAppLead(isOldDemo && leadId ? leadId : 0)

   // Handler for enabling/disabling demo inbox app
   const handleDemoInboxAppEnabledToggle = async (checked: boolean) => {
      if (!leadId || isLoadingDemoInboxAppEnabled) return
      try {
         await ConversationService.setDemoInboxAppEnabled(leadId, checked)
         await mutateDemoInboxAppEnabled()
         message.success(`Demo Inbox App ${checked ? 'enabled' : 'disabled'}!`)
      } catch (error) {
         message.error('Failed to update Demo Inbox App status.')
      }
   }

   const [demoInboxAppLeadIdInput, setDemoInboxAppLeadIdInput] = useState('')
   useEffect(() => {
      setDemoInboxAppLeadIdInput(
         demoInboxAppLeadData?.leadId ? String(demoInboxAppLeadData.leadId) : ''
      )
   }, [demoInboxAppLeadData])

   const [isSavingDemoInboxAppLeadId, setIsSavingDemoInboxAppLeadId] =
      useState(false)
   const handleSaveDemoInboxAppLeadId = async () => {
      if (
         !leadId ||
         !demoInboxAppLeadIdInput.trim() ||
         isSavingDemoInboxAppLeadId
      )
         return
      setIsSavingDemoInboxAppLeadId(true)
      try {
         await ConversationService.setDemoInboxAppLead(
            leadId,
            Number(demoInboxAppLeadIdInput.trim())
         )
         await mutateDemoInboxAppLead()
         message.success('Demo Inbox App Lead ID saved!')
      } catch (error) {
         message.error('Failed to save Demo Inbox App Lead ID.')
      } finally {
         setIsSavingDemoInboxAppLeadId(false)
      }
   }

   useEffect(() => {
      if (conversation) {
         updateConversation(conversation)
         setLastUpdate(new Date().toISOString())
         if (
            conversation[conversation?.length - 1]?.itemType === 'text' &&
            conversation[conversation?.length - 1]?.isIncoming
         ) {
            setIsNextConversationLoading(true)
         }
      }
   }, [conversation, updateConversation, setLastUpdate])

   useEffect(() => {
      const currentOrganization = LocalStorage.get('currentOrganization')
      if (!currentOrganization || !selectedLeadDetails?.userInfo?.id) return

      const eventSourceUrl = `${ENV.PUBLIC_FF_URL}sse/conversations?org=${currentOrganization}`
      let eventSource: EventSource | null = null
      let reconnectAttempts = 0
      const maxReconnectAttempts = 5

      function createEventSource() {
         if (eventSource) {
            eventSource.close()
            handleMutation()
         }

         eventSource = new EventSource(eventSourceUrl)

         eventSource.addEventListener('timeline', (event) => {
            const data = JSON.parse(event.data)
            if (data.personId === selectedLeadDetails?.userInfo?.id) {
               reconnectAttempts = 0
               handleMutation()
               mutateLeadDetails()

               if (isUserAtBottom()) {
                  shouldScrollToBottom.current = true
               }

               setIsNextConversationLoading(
                  data?.itemType === 'text' && data?.isIncoming
               )
            }
         })

         eventSource.onopen = () => {
            reconnectAttempts = 0
         }

         eventSource.onerror = () => {
            eventSource?.close()

            if (reconnectAttempts < maxReconnectAttempts) {
               reconnectAttempts++
               const backoff = Math.min(
                  3000 * Math.pow(1.5, reconnectAttempts),
                  30000
               )
               handleMutation()
               setTimeout(createEventSource, backoff)
            } else {
               console.log('Connection lost. Refreshing...')
            }
         }
      }

      createEventSource()

      return () => {
         eventSource?.close()
      }
   }, [selectedLeadDetails, mutateLeadDetails])

   useEffect(() => {
      if (loaderTimeoutRef.current) {
         clearTimeout(loaderTimeoutRef.current)
         loaderTimeoutRef.current = null
      }

      if (isNextConversationLoading) {
         loaderTimeoutRef.current = setTimeout(() => {
            setIsNextConversationLoading(false)
         }, 20000)
      }

      return () => {
         if (loaderTimeoutRef.current) {
            clearTimeout(loaderTimeoutRef.current)
            loaderTimeoutRef.current = null
         }
      }
   }, [isNextConversationLoading])

   const handleLoadMore = async () => {
      setConversationLimit((prevLimit) => prevLimit + 10)
      await loadMore()
   }

   const handleMutation = useCallback(async () => {
      await mutate()
      setOptimisticMessages({})
      shouldScrollToBottom.current = true
      mutateLeadDetails()
   }, [mutate])

   useEffect(() => {
      if (isInitialLoad.current && scrollRef.current) {
         scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
   }, [selectedLeadDetails?.userInfo.id])

   useEffect(() => {
      if (
         selectedLeadDetails?.userInfo.id &&
         !isInitialLoad.current &&
         loading
      ) {
         message.loading('Loading conversation history...', 0)
      } else {
         message.destroy()
      }
   }, [selectedLeadDetails?.userInfo.id, loading])

   useEffect(() => {
      if (!window.location.href.includes('demo-conversations-old')) return
      const handleKeyPress = async (event: KeyboardEvent) => {
         if ((event.ctrlKey || event.metaKey) && event.key === 'ArrowRight') {
            const leadId = selectedLeadDetails?.userInfo?.id

            if (!leadId) return
            setIsNextConversationLoading(true)
            try {
               await ConversationService.requestNextConversation(Number(leadId))
            } catch (error) {
               console.error('Error requesting next conversation:', error)
               message.error('Failed to request next conversation')
               setIsNextConversationLoading(false)
            }
         } else if (
            (event.ctrlKey || event.metaKey) &&
            event.key === 'ArrowLeft'
         ) {
            const leadId = selectedLeadDetails?.userInfo?.id

            if (!leadId) return
            message.loading('Requesting previous conversation...')

            try {
               await ConversationService.requestPreviousConversation(
                  Number(leadId)
               )
               mutate()
               mutateLeadDetails()
            } catch (error) {
               console.error('Error requesting previous conversation:', error)
               message.error('Failed to request previous conversation')
            }
         } else if (
            (event.ctrlKey || event.metaKey) &&
            event.key === 'Backspace'
         ) {
            const leadId = selectedLeadDetails?.userInfo?.id

            if (!leadId) return
            message.loading('Requesting to reset conversation...')
            setIsNextConversationLoading(false)
            try {
               await ConversationService.resetConversation(Number(leadId))
               mutate()
               mutateLeadDetails()
            } catch (error) {
               console.error('Error requesting reset conversation:', error)
               message.error('Failed to request reset conversation')
            }
         } else if (
            (event.ctrlKey || event.metaKey) &&
            event.key === 'ArrowUp'
         ) {
            const leadId = selectedLeadDetails?.userInfo?.id

            if (!leadId) return
            message.loading('Requesting to reset realtor timeout...')
            try {
               await ConversationService.resetRealtorTimeout(Number(leadId))
               mutate()
               mutateLeadDetails()
            } catch (error) {
               console.error('Error requesting reset realtor timeout:', error)
               message.error('Failed to request reset realtor timeout')
            }
         }
      }

      document.addEventListener('keydown', handleKeyPress)
      return () => document.removeEventListener('keydown', handleKeyPress)
   }, [selectedLeadDetails?.userInfo?.id])

   const combinedMessages = useMemo(() => {
      const leadId = selectedLeadDetails?.userInfo.id
      if (!leadId) return conversation || []

      const realMessageIds = new Set(conversation?.map((msg) => msg.id) || [])
      const currentLeadOptimisticMessages = optimisticMessages[leadId] || []
      const validOptimisticMessages = currentLeadOptimisticMessages.filter(
         (msg) => !realMessageIds.has(msg.id)
      )
      const result = [...(conversation || []), ...validOptimisticMessages]

      return result
   }, [conversation, optimisticMessages, selectedLeadDetails?.userInfo.id])

   useLayoutEffect(() => {
      const scrollElement = scrollRef.current
      if (!scrollElement) return

      if (isLoadingMore.current) {
         const newScrollHeight = scrollElement.scrollHeight
         const scrollHeightDelta =
            newScrollHeight - previousScrollBottom.current
         scrollElement.scrollTop += scrollHeightDelta
         isLoadingMore.current = false
      } else if (shouldScrollToBottom.current || isInitialLoad.current) {
         const isNearBottom =
            scrollElement.scrollHeight -
               scrollElement.scrollTop -
               scrollElement.clientHeight <
            200

         if (isInitialLoad.current || isNearBottom) {
            requestAnimationFrame(() => {
               requestAnimationFrame(() => {
                  if (scrollElement) {
                     scrollElement.scrollTop = scrollElement.scrollHeight
                  }
                  shouldScrollToBottom.current = false
               })
            })
         }
      }
   }, [conversation, optimisticMessages, combinedMessages])

   const isUserAtBottom = useCallback(() => {
      const scrollElement = scrollRef.current
      if (!scrollElement) return false

      return (
         scrollElement.scrollHeight -
            scrollElement.scrollTop -
            scrollElement.clientHeight <
         200
      )
   }, [])

   function debounce(func: (...args: any[]) => void, wait: number) {
      let timeout: number | null = null
      return (...args: any[]) => {
         if (timeout) clearTimeout(timeout)
         timeout = window.setTimeout(() => {
            func(...args)
            timeout = null
         }, wait)
      }
   }

   const handleAppointmentStatusToggle = async (checked: boolean) => {
      const leadId = selectedLeadDetails?.userInfo?.id
      if (!leadId || isAppointmentStatusUpdating) return

      setIsAppointmentStatusUpdating(true)
      try {
         await ConversationService.updateSetAppointmentStatus(leadId, checked)
         await mutateAppointmentStatus()

         message.success(
            `Appointment status ${
               checked ? 'enabled' : 'disabled'
            } successfully!`
         )
      } catch (error) {
         console.error('Error updating appointment status:', error)
         message.error('Failed to update appointment status. Please try again.')
      } finally {
         setIsAppointmentStatusUpdating(false)
      }
   }

   const handleDemoToggle = async (checked: boolean) => {
      const leadId = selectedLeadDetails?.userInfo?.id
      if (!leadId || isUpdatingDemo) return

      setIsUpdatingDemo(true)
      try {
         await ConversationService.setDemoIncomingCallStatus(leadId, checked)
         await mutateDemoStatus()

         message.success(
            `Demo status ${checked ? 'enabled' : 'disabled'} successfully!`
         )
      } catch (error) {
         console.error('Error updating demo status:', error)
         message.error('Failed to update demo status. Please try again.')
      } finally {
         setIsUpdatingDemo(false)
      }
   }

   // Use useCallback to memoize the debounced function
   // eslint-disable-next-line react-hooks/exhaustive-deps
   const handleScroll = useCallback(
      debounce(async (event: React.UIEvent<HTMLDivElement>) => {
         const element = event.target as HTMLDivElement
         // Add a buffer zone to trigger loading earlier
         const scrollThreshold = 100 // pixels from top
         if (element.scrollTop <= scrollThreshold && hasMore && !loading) {
            if (isLoadingMore.current) return
            isLoadingMore.current = true

            const scrollElement = scrollRef.current
            if (!scrollElement) return

            previousScrollBottom.current = scrollElement.scrollHeight

            const firstVisibleMessage = Array.from(
               scrollElement.children[0].children
            ).find((child) => {
               const rect = child.getBoundingClientRect()
               return rect.top >= 0 && rect.bottom <= window.innerHeight
            }) as HTMLElement | undefined

            const firstVisibleMessageId =
               firstVisibleMessage?.getAttribute('data-message-id')

            setConversationLimit((prevLimit) => prevLimit + 10)
            await loadMore()

            requestAnimationFrame(() => {
               if (firstVisibleMessageId) {
                  const referenceMessage = document.querySelector(
                     `[data-message-id="${firstVisibleMessageId}"]`
                  )
                  if (referenceMessage) {
                     // eslint-disable-next-line no-extra-semi
                     ;(referenceMessage as HTMLElement).scrollIntoView({
                        block: 'center',
                     })
                  } else {
                     const newScrollHeight = scrollElement.scrollHeight
                     const scrollHeightDelta =
                        newScrollHeight - previousScrollBottom.current
                     scrollElement.scrollTop = scrollHeightDelta
                  }
               } else {
                  const newScrollHeight = scrollElement.scrollHeight
                  const scrollHeightDelta =
                     newScrollHeight - previousScrollBottom.current
                  scrollElement.scrollTop = scrollHeightDelta
               }
               isLoadingMore.current = false
            })
         }
      }, 200),
      [hasMore, loading, loadMore]
   )

   const addOptimisticMessage = (message: string, isBuyer: boolean) => {
      const leadId = selectedLeadDetails?.userInfo.id
      if (!leadId) return

      const optimisticMessage = {
         id: `temp-${Date.now()}`,
         itemType: 'text',
         isIncoming: false,
         content: message,
         ts: new Date().toISOString(),
         assistant: {
            avatar: user?.avatar || null,
            name: user?.firstName?.charAt(0) || '',
         },
      }

      if (isBuyer) {
         optimisticMessage.isIncoming = true
      } else {
         optimisticMessage.assistant = {
            avatar: selectedLeadDetails?.userInfo.avatar || null,
            name: selectedLeadDetails?.userInfo.firstName || '',
         }
      }
      setIsNextConversationLoading(true)
      setOptimisticMessages((prev) => ({
         ...prev,
         [leadId]: [...(prev[leadId] || []), optimisticMessage],
      }))
      shouldScrollToBottom.current = true
   }

   const handleSaveRealtorPhone = async () => {
      console.log('Save button clicked', selectedLeadDetails)
      const leadId = selectedLeadDetails?.userInfo.id
      if (!leadId || !phoneNumber.trim() || isSavingPhone) {
         console.log('Save blocked:', {
            leadId,
            phoneNumber: phoneNumber.trim(),
            isSavingPhone,
         })
         return
      }
      setIsSavingPhone(true)

      try {
         console.log(
            'Calling ConversationService.setDemoRealtorPhone with:',
            leadId,
            phoneNumber.trim()
         )
         await ConversationService.setDemoRealtorPhone(
            leadId,
            phoneNumber.trim()
         )
         message.success('Realtor number saved successfully!')
      } catch (error) {
         console.error('Save realtor number error:', error)
         message.error('Failed to save realtor number. Please try again.')
      } finally {
         setIsSavingPhone(false)
      }
   }

   const handleResetRealtorPhone = async () => {
      const leadId = selectedLeadDetails?.userInfo.id
      console.log('Reset button clicked', { leadId, isSavingPhone })
      if (!leadId || isSavingPhone) {
         console.log('Reset blocked:', { leadId, isSavingPhone })
         return
      }
      setIsSavingPhone(true)

      try {
         console.log(
            'Calling ConversationService.clearDemoRealtorPhone with:',
            leadId
         )
         await ConversationService.clearDemoRealtorPhone(leadId)
         setPhoneNumber('')
         message.success('Realtor number cleared successfully!')
      } catch (error) {
         console.error('Clear realtor number error:', error)
         message.error('Failed to clear realtor number. Please try again.')
      } finally {
         setIsSavingPhone(false)
      }
   }

   if (error) return <div>Error: {error}</div>
   return (
      <div className="flex flex-col h-full">
         <div className="flex items-center justify-between px-8 py-4">
            <p className="text-[14px] font-medium mr-4">Conversation History</p>

            {isOldDemo && (
               <div className="flex flex-col items-start justify-start w-full gap-y-2">
                  <div className="flex items-center gap-1">
                     <div className="w-full py-2 text-center rounded-m hover:bg-white/10 text-[12px] text-nowrap">
                        Set Appointment in Calendar :
                     </div>
                     <Switch
                        checked={appointmentStatus?.isAppointmentSetEnabled}
                        onChange={handleAppointmentStatusToggle}
                        loading={isAppointmentStatusUpdating}
                        disabled={!selectedLeadDetails?.userInfo?.id}
                        className="mr-4"
                        style={{
                           backgroundColor:
                              appointmentStatus?.isAppointmentSetEnabled
                                 ? 'rgba(48, 25, 52, 1)'
                                 : 'rgba(255, 255, 255, 0.1)',
                        }}
                     />
                     <div className="w-full py-2 text-center rounded-m hover:bg-white/10 text-[12px]">
                        Incoming Call :
                     </div>
                     <Switch
                        checked={demoStatus?.isDemo}
                        onChange={handleDemoToggle}
                        loading={isUpdatingDemo}
                        disabled={!selectedLeadDetails?.userInfo?.id}
                        className="mr-4"
                        style={{
                           backgroundColor: demoStatus?.isDemo
                              ? 'rgba(48, 25, 52, 1)'
                              : 'rgba(255, 255, 255, 0.1)',
                        }}
                     />
                     <input
                        type="text"
                        placeholder="Enter Realtor Test Number"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="px-2 py-1 text-[12px] bg-white/10 border border-white/20 rounded text-white placeholder:text-white/50 focus:outline-none focus:border-white/40 w-48"
                     />
                     <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                           console.log('Save button onClick triggered')
                           handleSaveRealtorPhone()
                        }}
                        disabled={!phoneNumber.trim() || isSavingPhone}
                        loading={isSavingPhone}
                        className="px-2 py-1 text-[10px] rounded transition-colors"
                     >
                        {isSavingPhone ? 'Saving...' : 'Save'}
                     </Button>
                     <Button
                        size="small"
                        onClick={() => {
                           console.log('Reset button onClick triggered')
                           handleResetRealtorPhone()
                        }}
                        disabled={isSavingPhone}
                        className="px-2 py-1 text-[10px] rounded transition-colors"
                     >
                        Reset
                     </Button>
                  </div>
                  <div className="flex items-center gap-2">
                     {/* Demo Inbox App Switch */}
                     <div className="w-full py-2 text-center rounded-m hover:bg-white/10 text-[12px] text-nowrap">
                        Demo Inbox App:
                     </div>
                     <Switch
                        checked={!!demoInboxAppEnabledData?.isEnabled}
                        onChange={handleDemoInboxAppEnabledToggle}
                        loading={isLoadingDemoInboxAppEnabled}
                        disabled={!leadId}
                        className="mr-4"
                        style={{
                           backgroundColor: demoInboxAppEnabledData?.isEnabled
                              ? 'rgba(48, 25, 52, 1)'
                              : 'rgba(255, 255, 255, 0.1)',
                        }}
                     />
                     {/* Demo Inbox App Lead ID Field */}
                     {!!demoInboxAppEnabledData?.isEnabled && (
                        <>
                           <input
                              type="text"
                              placeholder="Enter Inbox App Lead ID"
                              value={demoInboxAppLeadIdInput}
                              onChange={(e) => {
                                 const value = e.target.value.replace(/\D/g, '')
                                 setDemoInboxAppLeadIdInput(value)
                              }}
                              className="px-2 py-1 text-[12px] bg-white/10 border border-white/20 rounded text-white placeholder:text-white/50 focus:outline-none focus:border-white/40 w-48"
                           />
                           <Button
                              type="primary"
                              size="small"
                              onClick={handleSaveDemoInboxAppLeadId}
                              disabled={
                                 !demoInboxAppLeadIdInput.trim() ||
                                 isSavingDemoInboxAppLeadId
                              }
                              loading={isSavingDemoInboxAppLeadId}
                              className="px-2 py-1 text-[10px] rounded transition-colors"
                           >
                              Save
                           </Button>
                        </>
                     )}
                  </div>
               </div>
            )}
         </div>
         <div
            data-test-id="conversation-history-scrollable-container"
            id="scrollableDiv"
            ref={scrollRef}
            className="relative flex flex-col flex-grow px-2 overflow-y-auto gap-y-8"
            onScroll={handleScroll}
         >
            {hasMore && !loading && (
               <button
                  onClick={handleLoadMore}
                  className="w-full py-2 text-center rounded-md bg-white/10 hover:bg-white/10 text-[12px]"
               >
                  Load previous conversation
               </button>
            )}

            {isInitialLoad.current ? (
               <div className="py-2 text-center">
                  <Spinner className="w-full" />
               </div>
            ) : (
               <div className="flex flex-col px-2 gap-y-6">
                  {combinedMessages.map((item: any, index: any) => {
                     const key = `${item.id}-${item.itemType}-${item.ts}-${index}`

                     if (
                        item.itemType === 'stage' ||
                        item.itemType === 'data_validation' ||
                        item.itemType === 'post_analysis'
                     ) {
                        return (
                           <StageChangeItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (item?.itemType === 'note') {
                        return (
                           <NotesItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (item?.itemType === 'event') {
                        return (
                           <EventItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (item.itemType === 'text' && !item.isIncoming) {
                        return (
                           <AgentMessageItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (item.itemType === 'text' && item.isIncoming) {
                        return (
                           <LeadMessageItem
                              key={key}
                              item={item}
                              selectedLeadDetails={selectedLeadDetails}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item?.itemType === 'call' &&
                        item?.content &&
                        !item.isIncoming &&
                        item.callEvents === null
                     ) {
                        return (
                           <CallLeadItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item?.itemType === 'call' &&
                        item?.itemActionStatus === 'missed'
                     ) {
                        return (
                           <VoiceMissedItem
                              key={key}
                              item={item}
                              selectedLeadDetails={selectedLeadDetails}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item?.itemType === 'voicemail' &&
                        !item.isIncoming &&
                        (item?.itemActionStatus === 'voicemail_missed' ||
                           item?.itemActionStatus === 'voicemail_sent')
                     ) {
                        return (
                           <VoiceMissedItem
                              key={key}
                              item={item}
                              selectedLeadDetails={selectedLeadDetails}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item.itemType === 'call' &&
                        !item.isIncoming &&
                        item.callEvents
                     ) {
                        return (
                           <CallSummaryAgentItem
                              key={key}
                              item={item}
                              selectedLeadDetails={selectedLeadDetails}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item?.itemType === 'call' &&
                        item?.isIncoming &&
                        item?.callEvents
                     ) {
                        return (
                           <CallSummaryLeadItem
                              key={key}
                              item={item}
                              selectedLeadDetails={selectedLeadDetails}
                              data-message-id={item.id}
                           />
                        )
                     } else if (item?.itemType === 'call' && item.isIncoming) {
                        return (
                           <CallAgentItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item?.itemType === 'email' &&
                        !item.isIncoming
                     ) {
                        return (
                           <EmailSentItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item.itemType === 'appointment' &&
                        item.appointment.status.includes('scheduled')
                     ) {
                        return (
                           <AppointmentScheduledItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (item.itemType === 'appointment_missed') {
                        return (
                           <AppointmentMissedItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item.itemType === 'appointment' &&
                        item.appointment.status === 'rescheduled'
                     ) {
                        return (
                           <AppointmentRescheduledItem
                              key={key}
                              item={item}
                              data-message-id={item.id}
                           />
                        )
                     } else if (
                        item.itemType === 'init_summary' &&
                        !item.isIncoming
                     ) {
                        return (
                           <InitialLeadSummaryItem
                              key={key}
                              item={item}
                              leadDetails={selectedLeadDetails}
                              data-message-id={item.id}
                           />
                        )
                     } else {
                        return null
                     }
                  })}
                  {isNextConversationLoading && <AgentLoaderItem />}
               </div>
            )}
            {loading && !isInitialLoad.current && (
               <div className="py-2 text-center">
                  <Spinner className="w-full" />
               </div>
            )}
         </div>
         {conversationInputValue && (
            <div className="flex items-center w-full pl-2 my-2">
               <MessageInput
                  leadId={selectedLeadDetails?.userInfo.id ?? null}
                  onOptimisticUpdate={addOptimisticMessage}
               />
            </div>
         )}
      </div>
   )
}
export default Conversation
